import * as React from "react";
import { parse, format } from "date-fns";
import { AlertTriangle, MessageSquareWarning, X } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { formatTime } from "../EntriesTable/EntriesTable";

import { EntryForm } from "./EntryForm";
import type { TimeEntryFormValues } from "./schema";
import type { Abbreviation } from "@/shared/components/NarrativeTextArea/NarrativeTextArea";

// ── Types ──────────────────────────────────────────────────────────

type Matter = {
  matter: string;
  matter_id: string;
  client: string;
  description: string;
  task_group: string;
  activity_group: string;
};

type Task = {
  task: string;
  task_description: string;
  task_group: string;
};

type Activity = {
  activity: string;
  activity_description: string;
  activity_group: string;
};

export type SelectedRowData = {
  id: number;
  date: string;
  matter_name: string;
  matter_id: string;
  description: string;
  duration: number;
  task: string;
  activity: string;
  document_name?: string;
  units_seconds?: number;
  start_time?: string;
  end_time?: string;
  application?: string;
};

type EditEntryV2Props = {
  onSave: (
    entry: {
      id: number;
      date: string;
      matter: string;
      matter_id: string;
      description: string;
      duration: number;
      task: string;
      activity: string;
    },
    shouldExport: boolean,
  ) => void;
  onDuplicate?: (
    entry: {
      date: string;
      matter: string;
      matter_id: string;
      description: string;
      duration: number;
      task: string;
      activity: string;
      document_name: string | null;
      client: string | null;
      units_seconds: number;
      start_time: string | null;
      end_time: string | null;
    },
    originalId: number,
  ) => Promise<void>;
  matters: Matter[];
  tasks: Task[];
  activities: Activity[];
  selectedRowData: SelectedRowData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  abbreviations?: Abbreviation[];
  attorneyFeedback?: string | null;
  onResolveFeedback?: () => Promise<void>;
  exportError?: string | null;
};

// ── Helpers ────────────────────────────────────────────────────────

function parseEntryDate(dateStr: string): Date {
  if (!dateStr) return new Date();
  // Try MM/dd/yyyy first (backend format), then fallback
  const parsed = parse(dateStr, "MM/dd/yyyy", new Date());
  if (!isNaN(parsed.getTime())) return parsed;
  // Try ISO or other formats
  const fallback = new Date(dateStr);
  return isNaN(fallback.getTime()) ? new Date() : fallback;
}

// ── Component ──────────────────────────────────────────────────────

export default function EditEntryV2({
  onSave,
  onDuplicate,
  matters,
  tasks,
  activities,
  selectedRowData,
  open,
  onOpenChange,
  abbreviations,
  attorneyFeedback,
  onResolveFeedback,
  exportError,
}: EditEntryV2Props) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [isResolvingFeedback, setIsResolvingFeedback] = React.useState(false);
  const [feedbackDismissed, setFeedbackDismissed] = React.useState(false);

  // Reset feedback dismissed state when entry changes
  React.useEffect(() => {
    setFeedbackDismissed(false);
  }, [selectedRowData.id]);

  // Close on Escape (only if no child dropdown is open)
  React.useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const openDropdowns = panelRef.current?.querySelectorAll(
          '[role="listbox"], [data-state="open"]',
        );
        if (openDropdowns && openDropdowns.length > 0) return;
        onOpenChange(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  // ── Build default values from selectedRowData ─────────────────

  const defaultValues = React.useMemo(
    () => ({
      date: parseEntryDate(selectedRowData.date),
      matter_id: selectedRowData.matter_id || "",
      matter: selectedRowData.matter_name || "",
      task: selectedRowData.task || "",
      activity: selectedRowData.activity || "",
      duration: selectedRowData.duration || 0,
      description: selectedRowData.description || "",
    }),
    [selectedRowData],
  );

  // ── Handlers ──────────────────────────────────────────────────

  const toSaveEntry = (values: TimeEntryFormValues) => ({
    id: selectedRowData.id,
    date: values.date ? format(values.date, "MM/dd/yyyy") : "",
    matter: values.matter,
    matter_id: values.matter_id,
    description: values.description,
    duration: values.duration,
    task: values.task,
    activity: values.activity,
  });

  const handleSave = React.useCallback(
    (values: TimeEntryFormValues) => {
      onSave(toSaveEntry(values), false);
      onOpenChange(false);
    },
    [onSave, onOpenChange, selectedRowData.id],
  );

  const handleExport = React.useCallback(
    (values: TimeEntryFormValues) => {
      onSave(toSaveEntry(values), true);
      onOpenChange(false);
    },
    [onSave, onOpenChange, selectedRowData.id],
  );

  const handleDuplicate = React.useCallback(
    async (values: TimeEntryFormValues) => {
      if (!onDuplicate) return;
      const selectedMatter = matters.find(
        (m) => m.matter_id === values.matter_id,
      );
      await onDuplicate(
        {
          date: values.date ? format(values.date, "MM/dd/yyyy") : "",
          matter: values.matter,
          matter_id: values.matter_id,
          description: values.description,
          duration: values.duration,
          task: values.task,
          activity: values.activity,
          document_name: selectedRowData.document_name || null,
          client: selectedMatter?.client || null,
          units_seconds: selectedRowData.units_seconds || 0,
          start_time: selectedRowData.start_time || null,
          end_time: selectedRowData.end_time || null,
        },
        selectedRowData.id,
      );
    },
    [onDuplicate, matters, selectedRowData],
  );

  const handleResolveFeedback = React.useCallback(async () => {
    if (!onResolveFeedback) return;
    setIsResolvingFeedback(true);
    try {
      await onResolveFeedback();
      setFeedbackDismissed(true);
    } finally {
      setIsResolvingFeedback(false);
    }
  }, [onResolveFeedback]);

  if (!open) return null;

  const showFeedback =
    attorneyFeedback && !feedbackDismissed;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80"
        onClick={() => onOpenChange(false)}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-full max-w-xl -translate-x-1/2 -translate-y-1/2",
          "max-h-[90vh] overflow-y-auto overflow-x-hidden",
          "rounded-lg border bg-card p-6 shadow-lg",
          "border-border text-foreground",
        )}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        {/* Header */}
        <div className="mb-4">
          <h2 className="text-lg font-sans font-semibold leading-none tracking-tight">
            Edit entry
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {selectedRowData.matter_id}
          </p>
          {selectedRowData.end_time && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {formatTime(selectedRowData.end_time)}
            </p>
          )}
        </div>

        {/* Export error banner */}
        {exportError && (
          <div className="mb-4 rounded-md border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">
                  Export Failed
                </p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-400/80">
                  {exportError}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Attorney feedback banner */}
        {showFeedback && (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 p-3">
            <div className="flex items-start gap-2">
              <MessageSquareWarning className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Admin Feedback
                </p>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-400/80">
                  {attorneyFeedback}
                </p>
              </div>
              {onResolveFeedback && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleResolveFeedback}
                  disabled={isResolvingFeedback}
                  className="shrink-0 h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/50"
                >
                  {isResolvingFeedback ? "Resolving..." : "Resolve"}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Form — key forces full remount when switching entries */}
        <EntryForm
          key={selectedRowData.id}
          matters={matters}
          tasks={tasks}
          activities={activities}
          currentDate={defaultValues.date}
          defaultValues={defaultValues}
          onSubmit={handleSave}
          onExport={handleExport}
          onDuplicate={onDuplicate ? handleDuplicate : undefined}
          submitLabel="Save changes"
          exportLabel="Save and export"
          documentName={selectedRowData.document_name || undefined}
          abbreviations={abbreviations}
          validateOnMount
        />
      </div>
    </div>
  );
}
